import { useEffect, useState } from "react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";
import { useApp } from "@/context/AppContext";
import { Panel, Loading, Empty, Kpi, Th, Td, TableWrap, NativeSelect } from "@/components/kit";
import { dmy, errMsg } from "@/lib/format";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const SERIES = [["views", "#111418"], ["leads", "#64748b"], ["reminders", "#d97706"], ["signups", "#B8000F"]];

const StatsChart = ({ series, t }) => (
  <div className="h-72" data-testid="demo-stats-chart">
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={series} margin={{ top: 8, right: 12, left: -16, bottom: 0 }}>
        <CartesianGrid stroke="#f1f5f9" vertical={false} />
        <XAxis dataKey="date" tickFormatter={(d) => d.slice(8, 10) + "/" + d.slice(5, 7)} tick={{ fontSize: 11 }} minTickGap={16} />
        <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
        <Tooltip labelFormatter={dmy} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        {SERIES.map(([k, c]) => <Line key={k} type="monotone" dataKey={k} name={t(`demo_stats_${k}`)} stroke={c} strokeWidth={2} dot={false} />)}
      </LineChart>
    </ResponsiveContainer>
  </div>
);

const LinkState = ({ l, t }) => l.revoked ? <span className="text-xs text-slate-400">{t("demo_revoked")}</span>
  : l.expires_at < new Date().toISOString() ? <span className="text-xs text-amber-700">{t("demo_expired")}</span> : null;

const StatsTable = ({ links, t }) => (
  <TableWrap>
    <thead className="border-b"><tr><Th>{t("demo_stats_link")}</Th><Th className="text-end">{t("demo_stats_views")}</Th><Th className="text-end">{t("demo_stats_leads")}</Th><Th className="text-end">{t("demo_stats_d2")}</Th><Th className="text-end">{t("demo_stats_d7")}</Th><Th className="text-end">{t("demo_stats_signups")}</Th><Th className="text-end">{t("demo_stats_rate")}</Th></tr></thead>
    <tbody>{links.map((l) => (
      <tr key={l.id} className="border-b border-slate-50" data-testid={`demo-stats-row-${l.slug}`}>
        <Td><b>{l.firm_name}</b> <LinkState l={l} t={t} /><div className="num text-[11px] text-slate-500">/demo/{l.slug} · {dmy(l.created_at)}</div></Td>
        <Td className="num text-end" data-testid={`demo-stats-views-${l.slug}`}>{l.views}</Td>
        <Td className="num text-end" data-testid={`demo-stats-leads-${l.slug}`}>{l.leads}</Td>
        <Td className="num text-end">{l.reminders_d2}</Td><Td className="num text-end">{l.reminders_d7}</Td>
        <Td className="num text-end font-semibold text-emerald-700" data-testid={`demo-stats-signups-${l.slug}`}>{l.signups}</Td>
        <Td className="num text-end font-semibold" data-testid={`demo-stats-rate-${l.slug}`}>{l.conversion_rate} %</Td>
      </tr>))}</tbody>
  </TableWrap>
);

const ReportToggle = () => {
  const { api, t } = useApp();
  const [on, setOn] = useState(null);
  useEffect(() => { api.get("/demo/prefs").then((r) => setOn(r.data.demo_monthly_report)).catch(() => {}); }, [api]);
  const flip = async (v) => { setOn(v); try { await api.put("/demo/prefs", { demo_monthly_report: v }); toast.success(t("saved")); } catch (e) { setOn(!v); toast.error(errMsg(e)); } };
  if (on === null) return null;
  return <label className="flex items-center gap-3 text-sm text-slate-700"><Switch checked={on} onCheckedChange={flip} data-testid="demo-monthly-report-toggle" />{t("demo_monthly_report")}</label>;
};

export const DemoStats = () => {
  const { api, t } = useApp();
  const [days, setDays] = useState(30);
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); api.get("/demo/stats", { params: { days } }).then((r) => setD(r.data)).catch(() => setD({ links: [], series: [], totals: {} })); }, [api, days]);
  const pick = <NativeSelect value={days} onChange={(e) => setDays(+e.target.value)} data-testid="demo-stats-days">{[30, 90].map((x) => <option key={x} value={x}>{x} {t("demo_stats_days")}</option>)}</NativeSelect>;
  if (!d) return <Loading />;
  const T = d.totals;
  return (
    <div className="space-y-6" data-testid="demo-stats">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <Kpi testid="demo-kpi-views" label={t("demo_stats_views")} value={T.views ?? 0} accent="navy" />
        <Kpi testid="demo-kpi-leads" label={t("demo_stats_leads")} value={T.leads ?? 0} />
        <Kpi testid="demo-kpi-reminders" label={t("demo_stats_reminders")} value={T.reminders ?? 0} accent="orange" />
        <Kpi testid="demo-kpi-signups" label={t("demo_stats_signups")} value={T.signups ?? 0} accent="red" />
        <Kpi testid="demo-kpi-rate" label={t("demo_stats_rate")} value={`${T.conversion_rate ?? 0} %`} accent="red" />
      </div>
      <Panel title={t("demo_tab_stats")} action={pick}><StatsChart series={d.series} t={t} /></Panel>
      <Panel title={t("demo_tab_links")}>{d.links.length === 0 ? <Empty /> : <StatsTable links={d.links} t={t} />}</Panel>
      <p className="text-xs text-slate-500">{t("demo_stats_help")}</p>
      <ReportToggle />
    </div>
  );
};
